<template>
    <div v-if="!loadingFtu">
        <DsModal
            class="campaign-sms-builder-modal-vue"
            is-open
            show-close
            size="fullscreen"
            :title="$t('funnel.edit.sideBar.flow.automatedSms')"
            @close="handleClose"
        >
            <template #header>
                <div class="header-actions">
                    <DsIconButton name="settings" @click="isSettingsModalOpen = true" />

                    <DsTextButton
                        :disabled="isSmsSendingBlockedByVerificationStatus"
                        dense
                        @click="handleSendTest"
                    >
                        {{ $t('funnel.campaign.communications.textmsg.sendtest.button') }}
                    </DsTextButton>


                    <DsFilledButton
                        class="done-button"
                        :disabled="isSmsSendingBlockedByVerificationStatus"
                        dense
                        @click="handleDone"
                    >
                        {{ $t('global.done') }}
                    </DsFilledButton>
                </div>
            </template>

            <template #default>
                <div class="write-stage">
                    <section class="content">
                        <div class="content-wrapper">
                            <div
                                v-if="isSmsSendingBlockedByVerificationStatus"
                                class="verification-status"
                            >
                                <KtvSmsVerificationStatusAlert
                                    render-if-sending-blocked
                                    show-button
                                    :message="automatedSmsAccountVerification.errorDescription"
                                    :rejection-date="automatedSmsAccountVerification.rejectionDate"
                                    :verification-status="automatedSmsAccount.verificationStatus"
                                />
                            </div>

                            <ErrorBanner v-if="showError && !isMessageValid" class="error-banner" :message="errorMessage" />

                            <section class="metadata">
                                <div class="from-container">
                                    <DsInputField
                                        class="sms-input"
                                        :label="$t('global.from')"
                                        readonly
                                        :value="fromNumber"
                                    />

                                    <DsPopover
                                        :is-open="showFromTooltip"
                                        position="right"
                                        position-fixed
                                        @close="showFromTooltip = false"
                                    >
                                        <template #reference>
                                            <DsIconButton
                                                class="tooltip-button"
                                                name="help-circle"
                                                @click="showFromTooltip = !showFromTooltip"
                                                @mouseover="showFromTooltip = true"
                                                @mouseleave="showFromTooltip = false"
                                            />
                                        </template>

                                        <template #default>
                                            <!-- eslint-disable vue/no-v-html -->
                                            <div class="from-tooltip" v-html="$t('funnel.campaign.communications.textmsg.from.tolltip')" />
                                            <!-- eslint-enable vue/no-v-html -->
                                        </template>
                                    </DsPopover>
                                </div>
                            </section>

                            <section class="message">
                                <span class="subtitle">{{ $t('funnel.campaign.communications.textmsg.header.label') }}</span>
                                <SmsTextBox
                                    v-model="messageContent"
                                    :has-error="showError && !isMessageValid"
                                    :app-id="appId"
                                    :user-id="userId"
                                    @input="handleUpdate"
                                />

                                <SmsInvalidCharacterAlert v-if="!messageContainsValidCharacters" />
                            </section>

                            <section class="audience">
                                <span class="phone-types-subtitle">{{ $t('funnel.campaign.communications.textmsg.phonenumber.text') }}</span>

                                <span class="phone-types-description"> {{ $t('funnel.campaign.communications.textmsg.selectmultiple.text') }} </span>

                                <SmsPhoneTypeSelector
                                    v-model="phoneTypes"
                                    class="phone-type-selector"
                                    :show-error="showError && !isPhoneTypesValid"
                                    @input="updatePhoneTypes"
                                />

                                <DsInlineAlert
                                    v-if="phoneTypes.length > 1"
                                    type="subtle"
                                    leading-icon
                                    class="multiple-phone-types-alert"
                                >
                                    <span>
                                        {{ $t('funnel.campaign.communications.textmsg.warningmsg') }}

                                        <!--TODO: Convert to ds-link once design system version updated-->
                                        <LinkButton
                                            class="alert-link"
                                            title="See your options"
                                            @click="isPhoneTypesHelpModalOpen = true"
                                        >
                                            {{ $t('funnel.campaign.communications.textmsg.warningmsg.link') }}
                                        </LinkButton>
                                    </span>
                                </DsInlineAlert>

                                <div>
                                    <span class="consent-title" :class="[{ 'consent-error': showConsentError }]">
                                        {{ $t('funnel.campaign.communications.kmn.consent.prompt.label') }}
                                    </span>
                                    <span class="consent-required">*</span>

                                    <DsCheckbox
                                        v-model="complianceCheckboxValue"
                                        class="consent-checkbox"
                                        :label="$t('funnel.campaign.communications.kmn.consent.permission.agreement.text')"
                                        :error="showConsentError"
                                        @input="updateConsent"
                                    />
                                </div>
                            </section>


                            <div class="sms-broadcast-footer-wrapper">
                                <div class="sms-broadcast-feedback">
                                    <span class="feedback-cta">
                                        {{ $t('funnel.campaign.communications.textmsg.missingtext') }}
                                    </span>

                                    <DsTextButton class="small" data-qa="feedback-button" @click="isFeedbackModalOpen = true">
                                        {{ $t('funnel.campaign.communications.textmsg.feedbacklink') }}
                                    </DsTextButton>
                                </div>

                                <SmsFooter />
                            </div>
                        </div>
                    </section>

                    <SmsPreview
                        class="preview"
                        :account="automatedSmsAccount"
                        :message="messageContent"
                        @show-settings="isSettingsModalOpen = true"
                    />
                </div>
            </template>
        </DsModal>

        <SmsAutomationFtuState v-if="accountLoaded && !setupComplete && !smsAccountLoadError && showFtuModal">
            <template #default="ftuProps">
                <FeatureFtuModal
                    is-open
                    :feature-name="ftuProps.feature"
                    :title="ftuProps.title"
                    :description="ftuProps.description"
                    :cta-button-text="ftuProps.cta"
                    :slides="ftuProps.tourSlides"
                    :tour-key="ftuProps.tourKey"
                    illustration="voice-messenger-color"
                    @close="handleClose"
                    @feature-ftu-action="handleTourComplete"
                    @complete="handleTourComplete"
                />
            </template>
        </SmsAutomationFtuState>

        <SmsSetupWorkflow
            v-if="showSmsSetupWorkflow"
            :from-number="fromNumber"
            :is-kmn-branded-opt-in-enabled="flagsInfo.isKmnBrandedOptInEnabled"
            :kmn-phone-verification-enabled="flagsInfo.kmnPhoneVerificationEnabled"
            :is-billing-changes-enabled="flagsInfo.billingChangesEnabled"
            @account-created="handleAccountCreated"
            @close="handleClose"
            @setup-complete="handleSetupComplete"
        />

        <KmnVerificationForm
            v-if="showKmnVerificationModal"
            :is-open="isKmnVerificationModalOpen"
            :kmn-account="automatedSmsAccount"
            :is-kmn-branded-opt-in-enabled="isKmnBrandedOptInEnabled"
            @complete="closeKmnVerificationModal"
            @close="handleClose"
        />

        <SmsSendTestModal
            :from="fromNumber"
            :is-open="isSendTestModalOpen"
            :message="messageContent"
            @close="isSendTestModalOpen = false"
        />

        <SmsMultiplePhoneAlertModal
            :is-open="isPhoneTypesHelpModalOpen"
            @close="isPhoneTypesHelpModalOpen = false"
        />

        <TextMarketingSettingsModal
            v-if="isSettingsModalOpen"
            :is-billing-changes-enabled="flagsInfo.billingChangesEnabled"
            :account="automatedSmsAccount || {}"
            @close="isSettingsModalOpen = false"
            @update="handleAccountUpdate"
        />

        <SmsErrorModal
            v-if="isErrorModalOpen"
            @close="handleErrorModalClose"
        />

        <SmsFeedbackModal v-if="isFeedbackModalOpen" source="Automations" @close="isFeedbackModalOpen = false" />

        <portal-target name="kmnTemplateManager" />

        <DsFocusOnKeyboardOnly />
    </div>
</template>

<script>
import translations from '@/i18n/en-us.json';
import axios from 'axios';
import debounce from 'lodash.debounce';

import ErrorBanner from '@/components/shared/ErrorBanner.vue';
import LinkButton from '@/components/shared/LinkButton.vue';

import SmsFooter from '@/components/automatedSms/SmsFooter.vue';
import SmsPreview from '@/components/automatedSms/SmsPreview.vue';
import SmsPhoneTypeSelector from '@/components/automatedSms/SmsPhoneTypeSelector.vue';
import SmsSendTestModal from '@/components/automatedSms/SmsSendTestModal.vue';
import SmsErrorModal from '@/components/automatedSms/SmsErrorModal.vue';
import SmsAutomationFtuState from '@/components/automatedSms/SmsAutomationFtuState.vue';
import SmsInvalidCharacterAlert from '@/components/automatedSms/SmsInvalidCharacterAlert.vue';
import SmsMultiplePhoneAlertModal from '@/components/automatedSms/SmsMultiplePhoneAlertModal.vue';

import SmsFeedbackModal from '@/components/shared/SmsFeedbackModal.vue';
import SmsSetupWorkflow from '@/components/automatedSms/setup/SmsSetupWorkflow.vue';
import TextMarketingSettingsModal from '@/components/automatedSms/TextMarketingSettingsModal.vue';
import KmnVerificationForm from '@/components/broadcast/sms/KmnVerificationForm.vue';
import SmsTextBox from '@/components/automatedSms/SmsTextBox.vue';

import { trackSmsAutomationCreated, trackSmsAccountProvisioningStart } from '@/components/automatedSms/smsAnalytics';
import { containsSupportedCharacters } from '@/components/automatedSms/automatedSms.util';

import FeatureFtuModal from '@/components/onboarding/components/FeatureFtuModal.vue';
import { SMS_MESSAGE_LENGTH, POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES } from '@/components/broadcast/constants/smsBroadcast.constants';
import KtvSmsVerificationStatusAlert from '@/components/automatedSms/KtvSmsVerificationStatusAlert.vue';

import { loadFtu } from '@/ftu.utils';
import { formatPhone } from '@/utils/phone.util';
import moment from 'moment-timezone';

export default {
    components: {
        SmsMultiplePhoneAlertModal,
        SmsPreview,
        SmsFooter,
        SmsSetupWorkflow,
        SmsSendTestModal,
        TextMarketingSettingsModal,
        SmsFeedbackModal,
        SmsPhoneTypeSelector,
        SmsErrorModal,
        SmsAutomationFtuState,
        FeatureFtuModal,
        LinkButton,
        SmsInvalidCharacterAlert,
        SmsTextBox,
        ErrorBanner,
        KmnVerificationForm,
        KtvSmsVerificationStatusAlert,
    },


    props: {
        cell: {
            type: Object,
            required: true,
        },

        flagsInfo: {
            type: Object,
            required: true,
        },

        funnelId: {
            type: Number,
            required: true,
        },

        appInfo: {
            type: Object,
            required: true,
        },

        debounceDelay: {
            type: Number,
            default: 500,
        },
        eventSource: {
            type: String,
            default: 'Automations',
        },
        isKmnBrandedOptInEnabled: Boolean,
    },

    emits: ['update-cell-value'],

    data() {
        return {
            automatedSmsAccount: null,
            accountLoaded: false,
            setupComplete: false,
            loading: false,
            automationMessageId: null,
            marketingAutomatedSmsId: null,
            messageContent: '',
            phoneTypes: [],
            consentConfirmationTime: null,
            consentAcceptedBy: null,
            showFromTooltip: false,
            isSendTestModalOpen: false,
            isSettingsModalOpen: false,
            isFeedbackModalOpen: false,
            isErrorModalOpen: false,
            isPhoneTypesHelpModalOpen: false,
            smsAccountLoadError: false,
            showError: false,
            showFtuModal: true,
            showSmsSetupWorkflow: false,
            ftu: null,
            loadingFtu: true,
            isKmnVerificationModalOpen: false,
            automatedSmsAccountVerification: {},
        };
    },

    computed: {
        isSpaApp() {
            return this.appInfo?.appEditionFlags?.spaApp;
        },

        userId() {
            return String(this.appInfo?.userId ?? 0);
        },

        appId() {
            return this.appInfo?.appName;
        },

        id() {
            return this.cell.value.marketingAutomatedSmsId;
        },

        complianceCheckboxValue: {
            set(value) {
                if (value) {
                    this.consentConfirmationTime = `${moment().unix()}`;
                } else {
                    this.consentConfirmationTime = null;
                }
            },
            get() {
                return Boolean(this.consentConfirmationTime);
            },
        },

        showConsentError() {
            return this.showError && !this.isConsentAccepted;
        },

        errorMessage() {
            if (this.isMessageTooLong) {
                return this.$t('sms.broadcast.campaignBuilder.error.pleaseShortenMessage', { count: this.maxCharacterCount });
            }

            return this.$t('funnel.campaign.communications.textmsg.warning.text');
        },

        fromNumber() {
            return this.automatedSmsAccount != null
                ? formatPhone(this.automatedSmsAccount.phoneNumber)
                : '';
        },

        isMessageTooLong() {
            return this.messageContent?.length > this.maxCharacterCount;
        },

        isMessageValid() {
            return this.messageContent?.length > 0 && !this.isMessageTooLong && this.messageContainsValidCharacters;
        },

        messageContainsValidCharacters() {
            return containsSupportedCharacters(this.messageContent);
        },

        isPhoneTypesValid() {
            return this.phoneTypes.length > 0;
        },

        isConsentAccepted() {
            return Boolean(this.consentConfirmationTime);
        },

        showKmnVerificationModal() {
            return this.flagsInfo.kmnPhoneVerificationEnabled && !this.showSmsSetupWorkflow && this.automatedSmsAccount?.phoneNumber && !this.automatedSmsAccount.isPhoneNumberVerified;
        },

        maxCharacterCount() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },

        cellReady() {
            return this.isMessageValid && this.isPhoneTypesValid && this.isConsentAccepted;
        },

        isSmsSendingBlockedByVerificationStatus() {
            return this.automatedSmsAccount?.verificationStatus
                && !POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES
                    .includes(this.automatedSmsAccount?.verificationStatus);
        },
    },

    watch: {
        showKmnVerificationModal: {
            handler(val) {
                this.isKmnVerificationModalOpen = val;
            },
        },

        complianceCheckboxValue(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.$nextTick(() => {
                    try {
                        const modalBody = this.$el.querySelector('.modal-overlay.campaign-sms-builder-modal-vue.open.fullscreen .modal-dialog.fullscreen .modal-content-wrapper .modal-body');

                        if (modalBody) {
                            modalBody.style.display = 'none';
                            modalBody.offsetHeight;
                            modalBody.style.display = '';
                        }
                    } catch (error) {
                        // error occurred while processing the modal body
                    }
                });
            }
        },

    },

    created() {
        if (this.debounceDelay > 0) {
            this.updateAutomationMessage = debounce(this.updateAutomationMessage, this.debounceDelay);
        }
    },

    mounted() {
        window.addEventListener('campaignSmsBuilderModalOpened', this.setModalTitle);
        this.onMounted();
    },

    beforeUnmount() {
        window.removeEventListener('campaignSmsBuilderModalOpened', this.setModalTitle);
    },

    methods: {
        async createAutomationMessage() {
            this.loading = true;

            try {
                const { data } = await axios.post('/app/automatedSmsFunnel', {
                    funnelId: this.funnelId,
                    phoneTypes: ['Mobile', 'Work', 'Home', 'Other'],
                });

                this.marketingAutomatedSmsId = data.marketingAutomatedSmsId;
                this.automationMessageId = data.automationMessageId;
                this.phoneTypes = ['Mobile', 'Work', 'Home', 'Other'];
                trackSmsAutomationCreated(this.eventSource, this.isSpaApp);
                window.parent.dispatchEvent(new CustomEvent('automationMessageCreated', {
                    detail: {
                        marketingAutomatedSmsId: this.marketingAutomatedSmsId,
                        automationMessageId: this.automationMessageId,
                    },
                }));
            } catch (e) {
                this.$toast({ message: this.$t('funnel.campiagn.communications.kmn.verification.messageLoading.error') });
            }

            this.loading = false;
        },

        handleClose() {
            if (!this.cellReady) {
                window.parent.dispatchEvent(new Event('automationMessageNotReady'));
            }
            this.saveConfig();
        },

        saveConfig() {
            const cell = { ...this.cell };

            cell.value = cell.value || {};

            cell.value.marketingAutomatedSmsId = this.marketingAutomatedSmsId;
            cell.value.automationMessageId = this.automationMessageId;

            this.$emit('update-cell-value', { cell, isReady: this.cellReady });
        },

        handleDone() {
            if (this.cellReady) {
                window.parent.dispatchEvent(new Event('automationMessageReady'));
                this.handleClose();
            } else {
                this.showError = true;
            }
        },

        handleSendTest() {
            if (this.isMessageValid) {
                this.isSendTestModalOpen = true;
            } else {
                this.showError = true;
            }
        },

        handleAccountCreated(account) {
            this.automatedSmsAccount = account;
            this.createAutomationMessage();
        },

        handleAccountUpdate(updatedAccount) {
            this.automatedSmsAccount = {
                ...this.automatedSmsAccount,
                ...updatedAccount,
            };
        },

        handleSetupComplete() {
            this.setupComplete = true;
        },

        async loadAutomatedSmsAccount() {
            this.loading = true;

            try {
                const { data } = await axios.get('/app/automatedSmsFunnel/getAccount');

                if (data) {
                    this.setupComplete = true;
                }

                this.automatedSmsAccount = data;
            } catch (e) {
                this.smsAccountLoadError = true;
                this.isErrorModalOpen = true;
            }

            this.loading = false;
        },

        async loadAutomationMessage(id) {
            this.loading = true;

            try {
                const { data } = await axios.get(`/app/automatedSmsFunnel/${id}`);

                this.marketingAutomatedSmsId = id;
                this.automationMessageId = data.automationMessageId;
                this.messageContent = data.message || '';
                this.phoneTypes = data.phoneTypes || ['Mobile', 'Work', 'Home', 'Other'];
                this.consentConfirmationTime = data.consentConfirmationTime || null;
                this.consentAcceptedBy = data.consentAcceptedBy || null;
            } catch (e) {
                this.$toast({ message: this.i18n('funnel.campiagn.communications.kmn.verification.messageLoading.error') });
            }

            this.loading = false;
        },

        async loadFtu() {
            this.loadingFtu = true;

            try {
                this.ftu = await loadFtu(this.appId, this.userId);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(err); // pending error state design
            } finally {
                this.loadingFtu = false;
            }
        },

        async onMounted() {
            Object.keys(translations).forEach((key) => {
                if (key.startsWith('funnel.campaign.communications.')) {
                    window.keapI18nMap[key] = translations[key];
                }
            });

            window.keapI18nMap['application.yes'] = translations['application.yes'];

            await this.loadFtu();

            await this.loadAutomatedSmsAccount();
            this.accountLoaded = true;

            if (!this.automatedSmsAccount) {
                return;
            }

            if (this.id) {
                await this.loadAutomationMessage(this.id);
            } else {
                await this.createAutomationMessage();
            }
        },

        setModalTitle(event) {
            this.modalTitle = event?.detail?.title || this.$t('funnel.campaign.communications.kmn.new.text.message.placeholder');
        },

        handleUpdate(message) {
            this.consentConfirmationTime = null;

            this.messageContent = message;

            if (this.isMessageValid && this.showError) {
                this.showError = false;
            }

            if (this.messageContainsValidCharacters) {
                this.updateAutomationMessage({ message, phoneTypes: this.phoneTypes, consentConfirmationTime: this.consentConfirmationTime });
            }
        },

        async updateAutomationMessage({ message, phoneTypes, consentConfirmationTime }) {
            this.loading = true;

            const consentAcceptedBy = consentConfirmationTime ? this.userId : null;

            try {
                await axios.put(`/app/automatedSmsFunnel/${this.marketingAutomatedSmsId}`, {
                    message, phoneTypes, consentConfirmationTime, consentAcceptedBy,
                });
                window.parent.dispatchEvent(new Event('automationMessageChanged'));
            } catch (e) {
                this.$toast({ message: this.$t('funnel.campiagn.communications.kmn.verification.messageUpdate.error') });
            }

            this.loading = false;
        },

        updatePhoneTypes(phoneTypes) {
            this.consentConfirmationTime = null;

            this.phoneTypes = phoneTypes;
            this.updateAutomationMessage({ message: this.messageContent, phoneTypes, consentConfirmationTime: this.consentConfirmationTime });

            if (this.isPhoneTypesValid && this.showError) {
                this.showError = false;
            }
        },

        updateConsent() {
            this.updateAutomationMessage({ message: this.messageContent, phoneTypes: this.phoneTypes, consentConfirmationTime: this.consentConfirmationTime });
        },

        handleErrorModalClose() {
            this.isErrorModalOpen = false;
            this.handleClose();
        },

        handleTourComplete() {
            trackSmsAccountProvisioningStart(this.eventSource, this.isSpaApp);
            this.showFtuModal = false;
            this.showSmsSetupWorkflow = true;
        },

        closeKmnVerificationModal() {
            this.isKmnVerificationModalOpen = false;

            this.automatedSmsAccount = {
                ...this.automatedSmsAccount,
                isPhoneNumberVerified: true,
            };
        },
    },
};
</script>

<style lang="scss">
    div.campaign-sms-builder-modal-vue {
        div.modal-header{
            padding: $spacing-100;
        }

        div.modal-body {
            padding: 0;
        }
    }
</style>

<style lang="scss" scoped>
    .campaign-sms-builder-modal-vue {
        --modal-padding: 0;
        overflow: hidden;
    }

    .header-actions {
        display: flex;
        gap: $spacing-100;
        align-items: center;
        padding: 0 $spacing-250;
    }

    .done-options {
        --button-padding-left: #{$spacing-100};
        --button-padding-right: #{$spacing-100};
    }

    .write-stage {
        display: flex;
        height: calc(100vh - #{px-to-rem(56px)});
    }

    .content {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 3;

        &-wrapper {
            padding-top: $spacing-400;
            height: 100%;
            width: 70%;
            display: flex;
            flex-direction: column;

            @media($small) {
                width: 100%;
                padding: $spacing-200 $spacing-300 0;
            }
        }
    }

    .error-banner {
        margin-bottom: $spacing-200;
    }

    section.metadata,
    section.audience,
    section.message {
        display: flex;
        flex-direction: column;
        margin-bottom: $spacing-200;
    }

    .title {
        font-family: $font-family-secondary-title;
        font-size: $font-size-xl;
        font-weight: $font-weight-bold;
        line-height: $line-height-xl;
        margin-bottom: $spacing-200;
    }

    .subtitle {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        font-weight: $font-weight-semibold;
        margin-bottom: $spacing-100;
    }

    .phone-types-description {
        font-size: $font-size-sm;
        margin-bottom: $spacing-200;
    }

    .phone-types-subtitle {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        font-weight: $font-weight-semibold;
        margin-bottom: $spacing-050;
    }

    .from-container {
        display: flex;
    }

    .tooltip-button {
        margin: $spacing-050 $spacing-100 0;

        --icon-color: #{$color-gray-darker};
        --icon-size: 1rem;
    }

    .from-tooltip {
        padding: $spacing-200;
        font-size: $font-size-md;
        line-height: $line-height-lg;
        .text-bold {
            font-weight: $font-weight-bold;
        }
    }

    .sms-input {
        max-width: px-to-rem(328px);
        width: 100%;

        @media($small) {
            max-width: initial;
        }
    }

    .sms-broadcast-footer-wrapper {
        white-space: nowrap;
        margin-top: auto;
    }

    .multiple-phone-types-alert {
        margin-bottom: $spacing-400;
        font-size: $font-size-sm;
    }

    .sms-broadcast-feedback {
        display: flex;
        align-items: center;
        font-size: $font-size-xs;
        gap: $spacing-200;

        .feedback-cta {
            margin-bottom: 0;
            color: $color-gray-700;
        }
    }

    .preview {
        flex: 2;
    }

    .alert-link {
        color: $color-text-normal;
        font-size: $font-size-md;
        text-decoration: underline;
        line-height: $line-height-lg;
        vertical-align: initial;
        font-weight: $font-weight-regular;
    }

    .consent-title {
        font-weight: $font-weight-bold;
        font-size: $font-size-md;

        &.consent-error {
            color: $color-red-800;
        }
    }

    .consent-required {
        color: $color-red-800;
    }

    .consent-checkbox {
        --checkbox-align-items: flex-start;
        font-size: $font-size-sm;
        line-height: $line-height-md;
    }

    .verification-status:not(:empty) {
        margin-bottom: $spacing-300;
        width: 100%;
    }
</style>
